'use client';

import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { theme } from '@startengine/design-system.theme.main';
import dynamic from 'next/dynamic';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import posthog from 'posthog-js';
import defaultAttributes from './bannerText/original';
import useIsApple from 'hooks/useIsApple';
import { trackEvent } from 'utils/analytics';
import useStrapiBanner from 'hooks/useStrapiBannerData';

const BannerCard = dynamic(() => import('../../BannerCard'), {
  loading: () => <Skeleton height={266} variant="rounded" />,
});

export const Banner = ({ preview: _preview }) => {
  const isTablet = useMediaQuery(theme.breakpoints.up('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isApple = useIsApple() && !isTablet && !isDesktop;

  const isMarketingPage = useSelector((state) => state.search.isMarketingPage);

  const {
    data,
    isLoading: loading,
    isValid: useStrapi,
  } = useStrapiBanner({ queryKey: 'explorePageCMSData', flag: 'explore-pages' });

  const attributes = !useStrapi || isMarketingPage ? defaultAttributes : data?.attributes;
  const hasStrapiImageUrl = Boolean(data?.attributes?.banner_image_desktop?.data?.attributes?.url);
  const useFullImagePath = hasStrapiImageUrl || typeof data === 'undefined' || data?.length === 0;

  const [sentPageView, setSentPageView] = useState(false);

  useEffect(() => {
    if (!loading && !sentPageView)
      if (useStrapi) {
        posthog?.capture('$pageview', {
          bannerSlug: attributes?.banner_button_url_desktop,
          smartBanner: false,
        });
        setSentPageView(true);
      }
  }, [loading, sentPageView, useStrapi, attributes?.banner_button_url_desktop]);

  const getImage = () => {
    if (typeof data === 'undefined' || data?.length === 0) {
      if (isDesktop) return `${attributes?.banner_image_desktop}`;
      if (isTablet) return `${attributes?.banner_image_tablet}`;
      if (isApple) return `${attributes?.banner_image_ios}`;

      return `${attributes?.banner_image_mobile}`;
    }
    if (isDesktop) return attributes?.banner_image_desktop?.data?.attributes?.url || '';
    if (isTablet) return attributes?.banner_image_tablet?.data?.attributes?.url || '';
    if (isApple) return attributes?.banner_image_ios?.data?.attributes?.url || '';

    return attributes?.banner_image_mobile?.data?.attributes?.url || '';
  };

  // Helper function to check if a value is valid (not null, undefined, empty, or 'undefined' string)
  const isValidText = (value) =>
    value !== null && value !== undefined && value !== '' && value !== 'undefined';

  // Helper function to get text with fallback to default
  const getTextWithFallback = (strapiValue, defaultValue) =>
    isValidText(strapiValue) ? strapiValue : defaultValue;

  const getTitle = () => {
    if (isDesktop) {
      return getTextWithFallback(
        attributes?.banner_title_desktop,
        defaultAttributes.banner_title_desktop,
      );
    }
    if (isTablet) {
      return getTextWithFallback(
        attributes?.banner_title_tablet,
        defaultAttributes.banner_title_tablet,
      );
    }
    if (isApple) {
      return getTextWithFallback(attributes?.banner_title_ios, defaultAttributes.banner_title_ios);
    }
    return getTextWithFallback(
      attributes?.banner_title_mobile,
      defaultAttributes.banner_title_mobile,
    );
  };

  const getSubtitle = () => {
    if (isDesktop) {
      return getTextWithFallback(
        attributes?.banner_subtitle_desktop,
        defaultAttributes.banner_subtitle_desktop,
      );
    }
    if (isTablet) {
      return getTextWithFallback(
        attributes?.banner_subtitle_tablet,
        defaultAttributes.banner_subtitle_tablet,
      );
    }
    if (isApple) {
      return getTextWithFallback(
        attributes?.banner_subtitle_ios,
        defaultAttributes.banner_subtitle_ios,
      );
    }
    return getTextWithFallback(
      attributes?.banner_subtitle_mobile,
      defaultAttributes.banner_subtitle_mobile,
    );
  };

  const getButtonText = () => {
    if (isDesktop) {
      return getTextWithFallback(
        attributes?.banner_button_text_desktop,
        defaultAttributes.banner_button_text_desktop,
      );
    }
    if (isTablet) {
      return getTextWithFallback(
        attributes?.banner_button_text_tablet,
        defaultAttributes.banner_button_text_tablet,
      );
    }
    if (isApple) {
      return getTextWithFallback(
        attributes?.banner_button_text_ios,
        defaultAttributes.banner_button_text_ios,
      );
    }
    return getTextWithFallback(
      attributes?.banner_button_text_mobile,
      defaultAttributes.banner_button_text_mobile,
    );
  };

  const getDisclaimerText = () => {
    if (isDesktop) {
      return getTextWithFallback(
        attributes?.banner_disclaimer_desktop,
        defaultAttributes.banner_disclaimer_desktop,
      );
    }
    if (isTablet) {
      return getTextWithFallback(
        attributes?.banner_disclaimer_tablet,
        defaultAttributes.banner_disclaimer_tablet,
      );
    }
    if (isApple) {
      return getTextWithFallback(
        attributes?.banner_disclaimer_ios,
        defaultAttributes.banner_disclaimer_ios,
      );
    }
    return getTextWithFallback(
      attributes?.banner_disclaimer_mobile,
      defaultAttributes.banner_disclaimer_mobile,
    );
  };

  const getButtonUrl = () => {
    if (isDesktop) {
      return getTextWithFallback(
        attributes?.banner_button_url_desktop,
        defaultAttributes.banner_button_url_desktop,
      );
    }
    if (isTablet) {
      return getTextWithFallback(
        attributes?.banner_button_url_tablet,
        defaultAttributes.banner_button_url_tablet,
      );
    }
    if (isApple) {
      return getTextWithFallback(
        attributes?.banner_button_url_ios,
        defaultAttributes.banner_button_url_ios,
      );
    }
    return getTextWithFallback(
      attributes?.banner_button_url_mobile,
      defaultAttributes.banner_button_url_mobile,
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          height: '266px',
          width: '100%',
        }}
      >
        <Skeleton height={266} variant="rounded" animation="wave" />
      </Box>
    );
  }

  // Always show the banner - use default attributes if no Strapi data available

  return (
    <BannerCard
      priority
      image={`${getImage()}`}
      imgTitle={getTitle()}
      imgSubtitle={getSubtitle()}
      buttonText={getButtonText()}
      buttonLink={getButtonUrl()}
      importantMessage={getDisclaimerText()}
      useFullImagePath={useFullImagePath}
      handleFunction={(e) => {
        e.preventDefault();
        trackEvent('clicked_explore_banner_cta', {
          slug: getButtonUrl(),
          link: getButtonUrl() || '',
          variant: 'control',
        });
        window.location.href = getButtonUrl();
      }}
    />
  );
};

export default Banner;
